import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Tag, Plus, Trash2, Edit2, Search, ShieldCheck, Copy } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';

export default function AdminCodes() {
  const { token, API } = useAuth();
  const [codes, setCodes] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    code: '', creator_name: '', store: '', store_domain: '',
    discount_type: 'percentage', discount_value: '', description: '',
    success_rate: '95', verified: true
  });

  const fetchCodes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/codes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { search }
      });
      setCodes(res.data.codes);
      setTotal(res.data.total);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [API, token, search]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const resetForm = () => {
    setForm({ code: '', creator_name: '', store: '', store_domain: '', discount_type: 'percentage', discount_value: '', description: '', success_rate: '95', verified: true });
    setEditingCode(null);
  };

  const openEdit = (c) => {
    setForm({
      code: c.code, creator_name: c.creator_name, store: c.store,
      store_domain: c.store_domain, discount_type: c.discount_type,
      discount_value: String(c.discount_value || ''), description: c.description || '',
      success_rate: String(c.success_rate), verified: c.verified
    });
    setEditingCode(c);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      discount_value: form.discount_value ? parseFloat(form.discount_value) : 0,
      success_rate: parseFloat(form.success_rate),
    };
    try {
      if (editingCode) {
        await axios.put(`${API}/admin/codes/${editingCode.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API}/admin/codes`, payload, { headers: { Authorization: `Bearer ${token}` } });
      }
      resetForm();
      setShowForm(false);
      fetchCodes();
    } catch (e) { alert(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  const deleteCode = async (id) => {
    if (!window.confirm('Delete this code?')) return;
    try {
      await axios.delete(`${API}/admin/codes/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchCodes();
    } catch (e) { alert('Failed'); }
  };

  const toggleActive = async (code) => {
    try {
      await axios.put(`${API}/admin/codes/${code.id}`, { active: !code.active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchCodes();
    } catch (e) { alert('Failed'); }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            data-testid="admin-codes-search"
            placeholder="Search codes, creators, stores..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="bg-[#121214] border-white/10 text-white pl-10 h-10"
          />
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-zinc-500">{total} codes</span>
          <button
            data-testid="admin-add-code-btn"
            onClick={() => { resetForm(); setShowForm(true); }}
            className="honey-btn px-4 py-2 rounded-lg text-xs font-bold flex items-center gap-2"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Code
          </button>
        </div>
      </div>

      {/* Codes Table */}
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Code</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Creator</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Store</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Discount</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Success</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Uses</th>
                <th className="text-left text-xs font-medium text-zinc-500 px-5 py-3">Status</th>
                <th className="text-right text-xs font-medium text-zinc-500 px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {codes.map((c, i) => (
                <tr key={c.id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`admin-code-row-${i}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-mono font-bold text-white bg-white/5 px-2 py-0.5 rounded">{c.code}</span>
                      {c.verified && <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />}
                    </div>
                  </td>
                  <td className="px-5 py-3 text-sm text-zinc-300">{c.creator_name}</td>
                  <td className="px-5 py-3">
                    <div>
                      <p className="text-xs text-zinc-300">{c.store}</p>
                      <p className="text-[10px] text-zinc-600">{c.store_domain}</p>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    {c.discount_value > 0 ? (
                      <span className="text-sm font-mono text-purple-400">
                        {c.discount_value}{c.discount_type === 'percentage' ? '%' : '$'}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-600">-</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-xs font-mono ${c.success_rate >= 90 ? 'text-emerald-400' : c.success_rate >= 70 ? 'text-amber-400' : 'text-red-400'}`}>
                      {c.success_rate}%
                    </span>
                  </td>
                  <td className="px-5 py-3 text-xs text-zinc-400 font-mono">{c.uses || 0}</td>
                  <td className="px-5 py-3">
                    <button onClick={() => toggleActive(c)} className={`text-xs px-2 py-0.5 rounded-full ${c.active ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>
                      {c.active ? 'Active' : 'Off'}
                    </button>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-md text-zinc-500 hover:text-amber-400 hover:bg-amber-500/10" data-testid={`admin-edit-code-${i}`}>
                        <Edit2 className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => deleteCode(c.id)} className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10" data-testid={`admin-delete-code-${i}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {codes.length === 0 && (
          <div className="text-center py-12">
            <Tag className="w-8 h-8 text-zinc-700 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">No creator codes yet</p>
          </div>
        )}
      </div>

      {/* Add/Edit Code Dialog */}
      <Dialog open={showForm} onOpenChange={() => { setShowForm(false); resetForm(); }}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingCode ? 'Edit Code' : 'Add Creator Code'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Code *</Label>
                <Input data-testid="admin-code-input" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase()})} placeholder="SAVE20" className="bg-[#09090B] border-white/10 text-white mt-1 font-mono" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Creator Name *</Label>
                <Input data-testid="admin-code-creator" value={form.creator_name} onChange={e => setForm({...form, creator_name: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Store *</Label>
                <Input data-testid="admin-code-store" value={form.store} onChange={e => setForm({...form, store: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Store Domain *</Label>
                <Input data-testid="admin-code-domain" value={form.store_domain} onChange={e => setForm({...form, store_domain: e.target.value})} placeholder="amazon.com" className="bg-[#09090B] border-white/10 text-white mt-1" required />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs text-zinc-500">Type</Label>
                <Select value={form.discount_type} onValueChange={v => setForm({...form, discount_type: v})}>
                  <SelectTrigger className="bg-[#09090B] border-white/10 text-white mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10">
                    <SelectItem value="percentage" className="text-white">% Off</SelectItem>
                    <SelectItem value="fixed" className="text-white">$ Off</SelectItem>
                    <SelectItem value="free_shipping" className="text-white">Free Ship</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Discount</Label>
                <Input type="number" step="0.5" value={form.discount_value} onChange={e => setForm({...form, discount_value: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
              <div>
                <Label className="text-xs text-zinc-500">Success %</Label>
                <Input type="number" value={form.success_rate} onChange={e => setForm({...form, success_rate: e.target.value})} className="bg-[#09090B] border-white/10 text-white mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-500">Description</Label>
              <Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Save on first order..." className="bg-[#09090B] border-white/10 text-white mt-1" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.verified} onCheckedChange={v => setForm({...form, verified: v})} className="data-[state=checked]:bg-emerald-500" />
              <Label className="text-xs text-zinc-400">Verified code</Label>
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => { setShowForm(false); resetForm(); }} className="flex-1 px-4 py-2 rounded-lg text-sm bg-white/5 text-zinc-400 hover:bg-white/10">Cancel</button>
              <button type="submit" data-testid="admin-code-submit" disabled={saving} className="flex-1 honey-btn px-4 py-2 rounded-lg text-sm font-bold disabled:opacity-50">
                {saving ? 'Saving...' : editingCode ? 'Update' : 'Add Code'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
